import { createContext, useContext, PropsWithChildren, useState, useEffect, useCallback } from 'react';
import { CheckboxInputProps } from '@/components/functional/CheckboxInput/CheckboxInput.tsx';

type CheckboxGroupContextProps = {
  value: any[];
  setValue: (value: any) => void;
  error?: string;
} & Pick<CheckboxInputProps, 'color'>;

const CheckboxGroupContext = createContext<CheckboxGroupContextProps>(undefined!);

export type CheckboxGroupProviderProps<T extends any[]> = {
  value?: T;
  onChange?: (value: T) => void;
  error?: string;
} & Pick<CheckboxInputProps, 'color'>;

export function CheckboxGroupProvider<T extends any[]>({
  children,
  value,
  onChange,
  error,
  color,
}: PropsWithChildren<CheckboxGroupProviderProps<T>>) {
  const [localValue, setLocalValue] = useState(value ?? ([] as unknown as T));

  const setLocalValueWrapper = useCallback(
    (newValue: any) => {
      let newLocalValue: T;
      if (localValue?.includes(newValue)) {
        newLocalValue = localValue.filter((val) => val !== newValue) as unknown as T;
      } else {
        newLocalValue = [...localValue, newValue] as unknown as T;
      }

      setLocalValue(newLocalValue);
      if (onChange) {
        onChange(newLocalValue);
      }
    },
    [localValue, onChange]
  );

  useEffect(() => {
    setLocalValue(value ?? ([] as unknown as T));
  }, [value]);

  return (
    <CheckboxGroupContext.Provider
      value={{
        value: localValue,
        setValue: setLocalValueWrapper,
        error,
        color,
      }}
    >
      {children}
    </CheckboxGroupContext.Provider>
  );
}

export function useCheckboxGroupContext(): CheckboxGroupContextProps {
  const context = useContext(CheckboxGroupContext);

  if (typeof context === 'undefined') {
    throw new Error('useCheckboxGroupContext should be used within the CheckboxGroupContext provider!');
  }

  return context;
}
